import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { api } from '../lib/api';
import { Badge, Button } from '../atoms';
import { DataTable, StatCard, StatCardRow, Tabs, GenerateEmailModal } from '../organisms';
import { stageLabel } from '../lib/appeal-stages';
import { formatDate } from '../lib/orgTimezone';

/**
 * AppealsQueues — /appeals/queues
 *
 * The appeals worklist, titled to the real lifecycle stages an operator works:
 * Pending Board Orders (awaiting_board_order), Hearing Notices
 * (hearing_notice_received), and Litigation (litigation_window + litigation_filed),
 * plus the per-county progress rollup. One tab per queue, each backed by
 * GET /api/workflow/appeals/queues/:kind. A StatCardRow up top shows the count in
 * every queue at a glance; the active tab renders the full list.
 */

type QueueKind =
  | 'pending-board-orders'
  | 'hearing-notices'
  | 'litigation'
  | 'county-progress';

// Each per-appeal queue row carries the appeal `id` (the endpoint SELECTs a.*),
// so a row click opens that appeal at /appeals/:id — matching every entity list.
// Exported so the dashboard's on-page Appeals Work List reuses the exact row
// shape + column defs (below) rather than duplicating them.
export interface PendingBoardOrderRow {
  id: string;
  account_number: string;
  parcel_name: string;
  county: string;
  lifecycle_stage: string;
  board_order_expected_date: string | null;
  // Signed: > 0 past the expected date, <= 0 not yet due, null = no date on file.
  days_overdue: number | null;
}
export interface HearingNoticeRow {
  id: string;
  account_number: string;
  parcel_name: string;
  county: string;
  hearing_notice_date: string | null;
  hearing_date: string | null;
}
interface LitigationRow {
  id: string;
  account_number: string;
  parcel_name: string;
  lifecycle_stage: string;
  litigation_deadline_date: string | null;
  // null once filed / when no deadline is on file (litigation_filed rows).
  days_remaining: number | null;
}
// County progress is a per-county GROUP BY rollup, NOT a single appeal — there is
// no appeal id to open, so this table is intentionally left non-navigable.
interface CountyProgressRow {
  county: string;
  total: number;
  worked: number;
  percent_complete: number;
}

const TAB_META: Array<{ kind: QueueKind; label: string; empty: string }> = [
  { kind: 'pending-board-orders', label: 'Pending Board Orders', empty: 'No appeals are awaiting a board order.' },
  { kind: 'hearing-notices', label: 'Hearing Notices', empty: 'No appeals have a hearing notice awaiting scheduling.' },
  { kind: 'litigation', label: 'Litigation', empty: 'No appeals are in the litigation stages.' },
  { kind: 'county-progress', label: 'County progress', empty: 'No county progress to report.' },
];

function useQueue<T>(kind: QueueKind) {
  return useQuery<T[]>({
    queryKey: ['appeals-queue', kind],
    queryFn: ({ signal }) => api.get<T[]>(`/api/workflow/appeals/queues/${kind}`, signal),
  });
}

function fmtDate(v: string | null): string {
  return formatDate(v);
}

export const pendingBoardOrderColumns: ColumnDef<PendingBoardOrderRow>[] = [
  { accessorKey: 'account_number', header: 'Account #', cell: ({ getValue }) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)' }}>{String(getValue() ?? '—')}</span> },
  { accessorKey: 'parcel_name', header: 'Parcel', cell: ({ getValue }) => String(getValue() ?? '—') },
  { accessorKey: 'county', header: 'County', cell: ({ getValue }) => String(getValue() ?? '—') },
  { accessorKey: 'board_order_expected_date', header: 'Expected by', cell: ({ getValue }) => fmtDate(getValue() as string | null) },
  {
    accessorKey: 'days_overdue',
    header: 'Status',
    cell: ({ getValue }) => {
      const raw = getValue() as number | null;
      if (raw == null) return <Badge variant="neutral">Awaiting</Badge>;
      const d = Number(raw);
      if (d > 0) return <Badge variant="danger">{d} {d === 1 ? 'day' : 'days'} overdue</Badge>;
      const due = -d;
      return <Badge variant="neutral">{due === 0 ? 'Due today' : `Due in ${due} ${due === 1 ? 'day' : 'days'}`}</Badge>;
    },
  },
];

export const hearingNoticeColumns: ColumnDef<HearingNoticeRow>[] = [
  { accessorKey: 'account_number', header: 'Account #', cell: ({ getValue }) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)' }}>{String(getValue() ?? '—')}</span> },
  { accessorKey: 'parcel_name', header: 'Parcel', cell: ({ getValue }) => String(getValue() ?? '—') },
  { accessorKey: 'county', header: 'County', cell: ({ getValue }) => String(getValue() ?? '—') },
  { accessorKey: 'hearing_notice_date', header: 'Notice received', cell: ({ getValue }) => fmtDate(getValue() as string | null) },
  {
    accessorKey: 'hearing_date',
    header: 'Hearing date',
    cell: ({ getValue }) => {
      const v = getValue() as string | null;
      return v ? <Badge variant="info">{fmtDate(v)}</Badge> : <span style={{ color: 'var(--color-ink-3)' }}>Not scheduled</span>;
    },
  },
];

const litigationColumns: ColumnDef<LitigationRow>[] = [
  { accessorKey: 'account_number', header: 'Account #', cell: ({ getValue }) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)' }}>{String(getValue() ?? '—')}</span> },
  { accessorKey: 'parcel_name', header: 'Parcel', cell: ({ getValue }) => String(getValue() ?? '—') },
  {
    accessorKey: 'lifecycle_stage',
    header: 'Stage',
    cell: ({ getValue }) => {
      const s = String(getValue() ?? '');
      return <Badge variant={s === 'litigation_filed' ? 'info' : 'warning'}>{stageLabel(s)}</Badge>;
    },
  },
  { accessorKey: 'litigation_deadline_date', header: 'Deadline', cell: ({ getValue }) => fmtDate(getValue() as string | null) },
  {
    accessorKey: 'days_remaining',
    header: 'Days remaining',
    cell: ({ getValue }) => {
      const raw = getValue() as number | null;
      if (raw == null) return <span style={{ color: 'var(--color-ink-3)' }}>—</span>;
      const d = Number(raw);
      const variant = d <= 3 ? 'danger' : d <= 7 ? 'warning' : 'neutral';
      return <Badge variant={variant}>{d} {d === 1 ? 'day' : 'days'}</Badge>;
    },
  },
];

const countyColumns: ColumnDef<CountyProgressRow>[] = [
  { accessorKey: 'county', header: 'County', cell: ({ getValue }) => String(getValue() ?? '—') },
  { accessorKey: 'worked', header: 'Worked', cell: ({ getValue }) => Number(getValue() ?? 0).toLocaleString() },
  { accessorKey: 'total', header: 'Total', cell: ({ getValue }) => Number(getValue() ?? 0).toLocaleString() },
  {
    accessorKey: 'percent_complete',
    header: 'Progress',
    cell: ({ getValue }) => {
      const pct = Math.max(0, Math.min(100, Math.round(Number(getValue() ?? 0))));
      const variant = pct >= 100 ? 'success' : pct >= 50 ? 'info' : 'warning';
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', minWidth: 160 }}>
          <div style={{
            flex: 1,
            height: 8,
            borderRadius: 'var(--r-full)',
            background: 'var(--color-status-gray-bg)',
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${pct}%`,
              height: '100%',
              background: 'var(--brand-primary)',
            }} />
          </div>
          <Badge variant={variant}>{pct}%</Badge>
        </div>
      );
    },
  },
];

/**
 * ATA part 3 §7 (item 27): a template dropdown at the appeal stage, right on the work list. The
 * Email column opens the template picker scoped to that appeal (litigation settlement, refund check,
 * and whatever appeal-scope templates the firm loads), without leaving the queue.
 */
function withEmailAction<T extends { id: string }>(columns: ColumnDef<T>[], onEmail: (row: T) => void): ColumnDef<T>[] {
  return [
    ...columns,
    {
      id: 'email',
      header: 'Email',
      cell: ({ row }) => (
        <Button
          variant="secondary"
          size="sm"
          aria-label={`Email template for ${(row.original as { account_number?: string }).account_number ?? 'appeal'}`}
          onClick={(e) => { e.stopPropagation(); onEmail(row.original); }}
        >
          Email
        </Button>
      ),
    },
  ];
}

export function AppealsQueues() {
  const navigate = useNavigate();
  const [active, setActive] = useState<QueueKind>('pending-board-orders');
  const openAppeal = (row: { id: string }) => navigate(`/appeals/${row.id}`);
  const [emailAppeal, setEmailAppeal] = useState<{ id: string; label: string } | null>(null);
  const onEmail = (row: { id: string; account_number?: string; parcel_name?: string }) =>
    setEmailAppeal({ id: row.id, label: [row.parcel_name, row.account_number].filter(Boolean).join(' · ') });

  const boardOrders = useQueue<PendingBoardOrderRow>('pending-board-orders');
  const hearings = useQueue<HearingNoticeRow>('hearing-notices');
  const litigation = useQueue<LitigationRow>('litigation');
  const county = useQueue<CountyProgressRow>('county-progress');

  const counts: Record<QueueKind, number | undefined> = {
    'pending-board-orders': boardOrders.data?.length,
    'hearing-notices': hearings.data?.length,
    'litigation': litigation.data?.length,
    'county-progress': county.data?.length,
  };

  const activeQuery = { 'pending-board-orders': boardOrders, 'hearing-notices': hearings, 'litigation': litigation, 'county-progress': county }[active];
  const activeMeta = TAB_META.find(t => t.kind === active)!;

  // Each KPI card jumps to its queue tab — the number and the list it summarizes
  // are the same data, so the card should behave like the tab it stands for.
  const tabCard = (kind: QueueKind, label: string) => ({
    onClick: () => setActive(kind),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive(kind); }
    },
    role: 'button' as const,
    tabIndex: 0,
    'aria-label': `Show ${label}`,
    style: {
      cursor: 'pointer',
      ...(active === kind ? { boxShadow: '0 0 0 2px var(--brand-primary)' } : {}),
    },
  });

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>Appeals worklist</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>What needs a call or a check today, across every open appeal.</p>
      </header>

      <StatCardRow style={{ marginBottom: 'var(--sp-5)' }}>
        <StatCard label="Pending board orders" value={fmtCount(counts['pending-board-orders'])} caption="awaiting a board order" accent="red" {...tabCard('pending-board-orders', 'pending board orders')} />
        <StatCard label="Hearing notices" value={fmtCount(counts['hearing-notices'])} caption="notice received" accent="info" {...tabCard('hearing-notices', 'hearing notices')} />
        <StatCard label="Litigation" value={fmtCount(counts['litigation'])} caption="window + filed" accent="amber" {...tabCard('litigation', 'litigation')} />
        <StatCard label="Counties in progress" value={fmtCount(counts['county-progress'])} caption="with open work" accent="hunter" {...tabCard('county-progress', 'county progress')} />
      </StatCardRow>

      <div style={{ marginBottom: 'var(--sp-5)' }}>
        <Tabs
          items={TAB_META.map(t => ({ value: t.kind, label: t.label, count: counts[t.kind] }))}
          activeValue={active}
          onChange={v => setActive(v as QueueKind)}
        />
      </div>

      {activeQuery.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)' }}>Loading {activeMeta.label.toLowerCase()}…</p>
      ) : activeQuery.error ? (
        <div style={{
          padding: 'var(--sp-5)',
          background: 'var(--color-status-red-bg)',
          border: '1px solid var(--color-status-red)',
          color: 'var(--color-status-red)',
          borderRadius: 'var(--r-lg)',
        }}>
          Failed to load this queue: {(activeQuery.error as Error).message}
        </div>
      ) : active === 'pending-board-orders' ? (
        <DataTable data={boardOrders.data ?? []} columns={withEmailAction(pendingBoardOrderColumns, onEmail)} emptyMessage={activeMeta.empty} onRowClick={openAppeal} />
      ) : active === 'hearing-notices' ? (
        <DataTable data={hearings.data ?? []} columns={withEmailAction(hearingNoticeColumns, onEmail)} emptyMessage={activeMeta.empty} onRowClick={openAppeal} />
      ) : active === 'litigation' ? (
        <DataTable data={litigation.data ?? []} columns={withEmailAction(litigationColumns, onEmail)} emptyMessage={activeMeta.empty} onRowClick={openAppeal} />
      ) : (
        // County progress rolls up open appeals by the parcel's county. Each row drills
        // into that county's parcels — a real parcels.county equality filter on the
        // parcels list (see ParcelsList ?county= deep link).
        <DataTable
          data={county.data ?? []}
          columns={countyColumns}
          emptyMessage={activeMeta.empty}
          onRowClick={(row) => {
            if (!row.county) return;
            navigate(`/parcels?county=${encodeURIComponent(row.county)}`);
          }}
        />
      )}
      {emailAppeal && (
        <GenerateEmailModal
          open
          onClose={() => setEmailAppeal(null)}
          scope={{ entityType: 'appeals', entityId: emailAppeal.id, label: emailAppeal.label }}
        />
      )}
    </>
  );
}

function fmtCount(n: number | undefined): string {
  return typeof n === 'number' ? String(n) : '…';
}
